import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Avatar,
  Icon,
  Typography,
  TYPOGRAPHY_VARIANT,
} from '@paypalcorp/ppods-react'
import { CoachTip, BodyText } from '@paypalcorp/pp-react'
import './style.css'
import { loggedNavMenuItems } from '../../config'
import { ExternalLink } from '../common/external-link'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useApplicationContext } from '../../context/ApplicationContext'
import { getUserType } from '../../utils/user-utils'
import { ACCOUNT_TYPE } from '../../common/constant'
import { recordDynamicEvent } from '../common/analytics/utils'
import {
  ContainerAppProvider,
  RemoteModule,
} from '@paypalcorp/module-federation'
import {
  Context as WorldReadyContext,
  Message,
} from '@paypalcorp/worldready-react'
import { SANDBOX_BANNER_EXCLUDED_ROUTES } from '../../config/excludedRoutes'
import { AlgoliaSearch } from '@paypalcorp/developersearchui'
import apiFactory from '../../utils/api-factory'
import { loadUserPreferences } from '../../helper/user-helper'
import logUtil from '../../utils/log-util'
import BannerModule from '../banner'

function getFullName(accountType, currentUser) {
  if (accountType === ACCOUNT_TYPE.IDENTITY) {
    return currentUser?.email || ''
  }
  return [currentUser?.firstName, currentUser?.lastName]
    .filter(Boolean)
    .join(' ')
}
const SWITCH_PROFILE = 'Switch Profiles'

const TopNavHeader = () => {
  const ref = useRef()
  const navigate = useNavigate()
  const worldReady = useContext(WorldReadyContext)
  const {
    user,
    logoutUrl,
    showSwitchProfile,
    showDeveloperInvite,
    csrf,
    profileSwitcherUrl,
    profileSwitcherUuid,
    setShowDrawer,
    showDrawer,
    enviroment,
    banner,
  } = useApplicationContext()
  const location = useLocation()
  const [showProfileSwitch, setShowProfileSwitch] = useState(false)
  const profileMenuItems = [
    ...loggedNavMenuItems,
    { title: 'Log Out', path: logoutUrl, target: '_self' },
  ]
  const currentUser = user
  const accountType = currentUser ? getUserType(currentUser) : {}
  const FULL_NAME = getFullName(accountType, currentUser)
  const isSandboxBannerHidden = SANDBOX_BANNER_EXCLUDED_ROUTES.includes(
    location.pathname,
  )
  const [height, setHeight] = useState(0)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownTimeout = React.useRef()
  const targetRefTopCenter = React.useRef()
  const [isMounted, setMounted] = useState(false)
  const [isCommCardClosed, setIsCommCardClosed] = useState(true)
  const COMM_INFOCARD_NAME = 'COMM_INFOCARD'
  const COMM_INFOCARD_VALUE = 'CLOSED'

  useEffect(() => {
    setHeight(ref?.current?.clientHeight)
  }, [ref?.current?.clientHeight])

  const handleMouseEnter = () => {
    clearTimeout(dropdownTimeout.current)
    setDropdownOpen(true)
  }

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setDropdownOpen(false), 120)
  }

  const closeSwitchProfile = () => {
    recordDynamicEvent(
      `switch_profile,click,closed_switch_profile_menu_closeButton`,
    )
    setShowProfileSwitch(false)
  }

  const openSheet = () => {
    recordDynamicEvent(`switch_profile,click,clicked_switch_profile_menu`)
    setShowProfileSwitch(true)
  }

  const addFPTI = (subItem) => {
    const excludeItem = ['Braintree GraphQL API', 'Demo portal', 'API status']
    if (!excludeItem.includes(subItem.title)) {
      const mainTitle = subItem.title
      const eventName = mainTitle
        .toLowerCase()
        .replace(/[&/]/g, '')
        .replace(/\s+/g, '_')
      recordDynamicEvent(
        `developer_dashboard,click,developer_dashboard_menu_${eventName}`,
      )
    }
  }

  const fptiData = {
    pgrp: 'nav-menu:dashboard',
    page: 'profile-switcher:dashboard',
  }

  const remoteConfig = {
    name: 'profile-switcher',
    url: profileSwitcherUrl,
    fallback: profileSwitcherUrl,
    module: './ProfileSwitcher',
    scope: 'profileSwitcher',
  }

  const handlerMyAccount = async () => {
    recordDynamicEvent(`notification_tooltip,click,tooltip_myaccount_clicked`)
    const userNotificationPreference = {
      preferenceName: COMM_INFOCARD_NAME,
      preferenceValue: COMM_INFOCARD_VALUE,
    }
    await apiFactory.addUserPreference(csrf, userNotificationPreference)
    setIsCommCardClosed(true)
    navigate('/accountStatus?source=notification')
    setMounted(false)
  }

  const getUserPreferences = async () => {
    try {
      const response = await apiFactory.getNotifications({ csrf })
      const channelStatusOff =
        response && response[0] && response[0].channels?.[0]?.status === 'OFF'
      if (channelStatusOff) {
        const preferences = await loadUserPreferences()
        const commPreference = preferences.find(
          (userPreference) => userPreference.devup_name === COMM_INFOCARD_NAME,
        )
        if (commPreference) {
          setIsCommCardClosed(
            commPreference.devup_value === COMM_INFOCARD_VALUE,
          )
          setMounted(false)
        } else {
          setIsCommCardClosed(false)
          setMounted(true)
        }
      }
    } catch (e) {
      logUtil.error(`error while loading get notification error ${e}`)
    }
  }

  useEffect(() => {
    if (accountType !== 'IDENTITY') {
      getUserPreferences()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tipContent = (
    <span className="">
      Open{' '}
      <BodyText
        style={{
          color: '#0070E0',
          cursor: 'pointer',
          display: 'inline-block',
        }}
        onClick={handlerMyAccount}
        target={'_self'}
      >
        <Message id="account-notifications.toltip.myaccount" />
      </BodyText>{' '}
      <Message id="account-notifications.toltip.content" />
    </span>
  )

  const toltipCloseHandler = () => {
    recordDynamicEvent(`notification_tooltip,close,tooltip_closeIcon_clicked`)
    setMounted(false)
  }

  const displaySandboxBanner =
    enviroment === 'sandbox' && user && !isSandboxBannerHidden

  return (
    <header
      className="top-nav-header"
      style={
        showDrawer
          ? { width: 'calc(-240px + 100vw)' }
          : { width: 'calc(0px + 100vw)' }
      }
    >
      <div className="top-nav-header__row">
        <div className="top-nav-header__left">
          <button
            className="top-nav-header__menu-btn"
            aria-label="Open menu"
            onClick={() => {
              setShowDrawer(!showDrawer)
            }}
          >
            <Icon name="menu" size="24" />
          </button>
          <Typography
            variant={TYPOGRAPHY_VARIANT.HEADING_SM}
            style={{
              margin: '0',
            }}
          >
            {FULL_NAME}
          </Typography>
        </div>
        <div className="top-nav-header__center">
          <AlgoliaSearch
            buttonStyle={{
              isSecondary: true,
              isInverse: true,
              size: 'xl',
            }}
            fptiOptions={{
              space_key: 'SKEXJE',
              component: 'devdashnodeweb',
            }}
          />
        </div>
        <div className="top-nav-header__right">
          <BodyText
            as={ExternalLink}
            href="https://www.paypal.com/mep/dashboard"
            target={'_blank'}
          >
            <Typography
              variant={'body'}
              style={{
                margin: '0',
                fontSize: '14px',
              }}
            >
              <Message id="home.business.dashboard" />
            </Typography>
          </BodyText>
          <span className="top-nav-header__divider" />
          <div
            className="top-nav-header__avatar-dropdown-wrapper"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            tabIndex={0}
          >
            <div
              ref={targetRefTopCenter}
              style={{ cursor: 'pointer' }}
              onClick={() => setMounted(true)}
            >
              <Avatar
                name={FULL_NAME}
                onClick={function lte() {}}
                onSubIconClick={function lte() {}}
                size="32"
                variant="primary"
              />
            </div>
            {!isCommCardClosed && isMounted && accountType !== 'IDENTITY' && (
              <CoachTip
                caretPos="top-right"
                text={tipContent}
                targetElementRef={targetRefTopCenter}
                isOpen={true}
                onClose={toltipCloseHandler}
                style={{ position: 'fixed' }}
              />
            )}
            {dropdownOpen && (
              <ul className="top-nav-header__dropdown">
                {profileMenuItems.map((subItem, subIdx) => {
                  if (subItem.title === SWITCH_PROFILE) {
                    return showSwitchProfile && showDeveloperInvite ? (
                      <li
                        className="top-nav-header__dropdown-item"
                        key={subIdx}
                        onClick={openSheet}
                      >
                        <BodyText
                          as={NavLink}
                          to={subItem.path}
                          target={subItem.target || '_self'}
                          key={subIdx}
                        >
                          {subItem.title}
                        </BodyText>
                      </li>
                    ) : null
                  }
                  const linkType = subItem.path.startsWith('https://') ? (
                    <BodyText
                      as={ExternalLink}
                      href={subItem.path}
                      target={subItem.target || '_blank'}
                      key={subIdx}
                      onClick={() => addFPTI(subItem)}
                    >
                      {subItem.title}
                    </BodyText>
                  ) : (
                    <BodyText
                      as={NavLink}
                      to={subItem.path}
                      target={subItem.target || '_self'}
                      key={subIdx}
                    >
                      {subItem.title}
                    </BodyText>
                  )

                  return (
                    <li className="top-nav-header__dropdown-item" key={subIdx}>
                      {linkType}
                    </li>
                  )
                })}
              </ul>
            )}
            {showProfileSwitch && (
              <ContainerAppProvider
                modules={{ 'profile-switcher': remoteConfig }}
              >
                <RemoteModule
                  config={remoteConfig}
                  disableHostOverride={true}
                  csrf={csrf}
                  showSwitcher={showProfileSwitch}
                  handleClose={closeSwitchProfile}
                  locale={worldReady}
                  isWidget={true}
                  fptiData={fptiData}
                  fnSessionId={profileSwitcherUuid}
                />
              </ContainerAppProvider>
            )}
          </div>
        </div>
      </div>
      {banner?.isActive && <BannerModule bannerInfo={banner} offset={height} />}
      {displaySandboxBanner && (
        <div className="top-nav-header__sandbox-banner">
          You’re in sandbox mode.
        </div>
      )}
    </header>
  )
}

export default TopNavHeader
